import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageMeta from "../../components/PageMeta";
import api from "../../utils/api";
import { getImageUrl, getProfileImageUrl } from "../../utils/imageUtils";
import { formatFarmerLocation } from "../../utils/addressUtils";
import {
  FaMapMarkerAlt,
  FaStar,
  FaRegStar,
  FaUser,
  FaLeaf,
  FaStore,
  FaTruck,
  FaSeedling,
} from "react-icons/fa";
import RoleIllustration from "../../components/RoleIllustration";
import { ROLE_IMG } from "../../utils/roleAssets";
import "./Landing.css";

const Landing = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedProducts();
    fetchFeaturedFarmers();
    fetchLatestReviews();
  }, []);

  const fetchLatestReviews = async () => {
    try {
      const response = await api.get("/reviews/latest?limit=6");
      const data = response.data || [];
      setReviews(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviews([]);
    }
  };

  // Preload first 2 product images for faster initial render
  useEffect(() => {
    if (products.length > 0) {
      const firstTwoImages = products
        .slice(0, 2)
        .filter((p) => p.image)
        .map((p) => {
          const link = document.createElement("link");
          link.rel = "preload";
          link.as = "image";
          link.href = p.image;
          return link;
        });

      firstTwoImages.forEach((link) => document.head.appendChild(link));

      return () => {
        firstTwoImages.forEach((link) => document.head.removeChild(link));
      };
    }
  }, [products]);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await api.get("/products?limit=6");
      const productsData = response.data || [];

      // Transform products to match frontend format
      const transformedProducts = productsData.slice(0, 6).map((product) => ({
        id: product._id,
        name: product.name,
        price: product.price,
        unit: product.unit,
        image:
          product.images && product.images.length > 0
            ? getImageUrl(product.images[0], {
                width: 500,
                height: 500,
                quality: 80,
                format: "auto",
                crop: "fill",
              })
            : null,
        farmer: product.farmer?.name || "Local Farmer",
        location:
          formatFarmerLocation(product.farmer) !== "N/A"
            ? formatFarmerLocation(product.farmer)
            : "Nepal",
        isOrganic: product.isOrganic || false,
        rating: product.rating || 0,
      }));

      setProducts(transformedProducts);
    } catch (error) {
      console.error("Error fetching featured products:", error);
      // Fallback to empty array if API fails
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedFarmers = async () => {
    try {
      // Fetch products to get unique farmers
      const response = await api.get("/products?limit=20");
      const productsData = response.data || [];

      // Get unique farmers from products
      const farmerMap = new Map();
      productsData.forEach((product) => {
        if (product.farmer && product.farmer._id) {
          const farmerId = product.farmer._id;
          if (!farmerMap.has(farmerId)) {
            farmerMap.set(farmerId, {
              id: farmerId,
              name: product.farmer.name || "Local Farmer",
              location:
                formatFarmerLocation(product.farmer) !== "N/A"
                  ? formatFarmerLocation(product.farmer)
                  : "Nepal",
              rating: 4.5, // Default rating
              image: product.farmer.profileImage || null,
              products: 0,
              username: product.farmer.username || farmerId,
            });
          }
          farmerMap.get(farmerId).products++;
        }
      });

      // Convert to array and take first 6
      const farmersArray = Array.from(farmerMap.values()).slice(0, 6);
      setFarmers(farmersArray);
    } catch (error) {
      console.error("Error fetching featured farmers:", error);
      setFarmers([]);
    }
  };

  return (
    <div className="landing-page">
      <PageMeta
        title={t('landing.metaTitle')}
        description={t('landing.metaDescription')}
        path="/"
      />
      <Header />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">{t("landing.heroTitle")}</h1>
            <p className="hero-subtitle">{t("landing.heroSubtitle")}</p>
            <div className="hero-cta">
              <Link to="/market" className="btn-primary-large">
                {t("landing.startShopping")}
              </Link>
              <Link to="/nearby" className="btn-secondary-large">
                {t("landing.findNearby")}
              </Link>
              <Link to="/signup?type=farmer" className="btn-secondary-large">
                {t("landing.becomeAFarmer")}
              </Link>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-image-wrapper">
              <div className="hero-image">
                <img src="/images/heroimage.png" alt="Fresh Produce" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="featured-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-header-content">
              <h2 className="section-title">{t("landing.freshPicks")}</h2>
              <p className="section-description">
                {t("landing.freshPicksDesc")}
              </p>
            </div>
            <Link to="/market" className="btn-view-all">
              {t("landing.viewAllProducts")}
            </Link>
          </div>

          {loading ? (
            <div className="products-loading">
              <div className="loading-spinner"></div>
              <p>{t("landing.loadingFreshPicks")}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="products-empty">
              <p>{t("landing.noProductsAvailable")}</p>
            </div>
          ) : (
            <div className="products-grid">
              {products.map((product, index) => (
                <div
                  key={product.id}
                  className="product-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Link
                    to={`/products/${product.id}`}
                    className="product-image-link"
                  >
                    <div className="product-image">
                      {product.isOrganic && (
                        <div className="organic-badge">
                          <FaLeaf className="organic-icon" />
                          <span className="organic-text">
                            {t("common.organic")}
                          </span>
                        </div>
                      )}
                      <div className="product-image-container loading">
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            loading={index < 3 ? "eager" : "lazy"}
                            decoding="async"
                            fetchpriority={index < 2 ? "high" : "auto"}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                            onLoad={(e) => {
                              e.target
                                .closest(".product-image-container")
                                ?.classList.remove("loading");
                            }}
                            onError={(e) => {
                              e.target
                                .closest(".product-image-container")
                                ?.classList.remove("loading");
                            }}
                          />
                        ) : (
                          <div
                            className="product-image-placeholder"
                            aria-hidden
                          >
                            <FaSeedling />
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    <div className="product-meta">
                      <div className="product-farmer-info">
                        <span className="product-farmer">
                          <FaUser /> {product.farmer}
                        </span>
                        <span className="product-location">
                          <FaMapMarkerAlt /> {product.location}
                        </span>
                      </div>
                      {product.rating > 0 && (
                        <div className="product-rating">
                          <span className="rating-stars">
                            <FaStar />
                          </span>
                          <span className="rating-value">
                            {product.rating.toFixed(1)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="product-footer">
                      <div className="product-price">
                        <span className="price-currency">₨</span>
                        <span className="price-amount">{product.price}</span>
                        <span className="price-unit">/{product.unit}</span>
                      </div>
                      <Link
                        to={`/products/${product.id}`}
                        className="btn-view-product"
                      >
                        {t("common.viewDetails")}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Farmers Section */}
      <section className="farmers-section">
        <div className="section-container">
          <h2 className="section-title">{t("landing.meetFarmers")}</h2>
          <p className="section-subtitle">{t("landing.meetFarmersDesc")}</p>
          <div className="farmers-grid">
            {farmers.map((farmer) => (
              <div key={farmer.id} className="farmer-card">
                <div className="farmer-avatar">
                  {farmer.image ? (
                    <img src={getImageUrl(farmer.image)} alt={farmer.name} />
                  ) : (
                    <div className="farmer-emoji">
                      <img
                        src={ROLE_IMG.farmer()}
                        alt=""
                        className="landing-farmer-fallback-img"
                      />
                    </div>
                  )}
                </div>
                <h3 className="farmer-name">{farmer.name}</h3>
                <p className="farmer-location">
                  <FaMapMarkerAlt /> {farmer.location}
                </p>
                <div className="farmer-rating">
                  {Array.from({ length: Math.floor(farmer.rating) }).map(
                    (_, i) => (
                      <FaStar key={i} />
                    ),
                  )}
                  <span className="rating-number">
                    {farmer.rating.toFixed(1)}
                  </span>
                </div>
                <p className="farmer-products">
                  {farmer.products}{" "}
                  {farmer.products === 1
                    ? t("common.product")
                    : t("common.products")}
                </p>
                <Link
                  to={`/users/${farmer.username || farmer.id}`}
                  className="btn-view-shop"
                >
                  {t("common.viewShop")}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section
        className="how-it-works-section"
        aria-labelledby="how-it-works-heading"
      >
        <div className="how-it-works-bg" aria-hidden />
        <div className="section-container how-it-works-inner">
          <header className="how-it-works-header">
            <h2 id="how-it-works-heading" className="how-it-works-title">
              {t("landing.howItWorks")}
            </h2>
            <p className="how-it-works-lede">{t("landing.howItWorksSub")}</p>
          </header>

          <ol className="how-it-works-track">
            {[
              {
                kind: "icon",
                Icon: FaStore,
                titleKey: "landing.step1Title",
                descKey: "landing.step1Desc",
              },
              {
                kind: "role",
                role: "farmer",
                titleKey: "landing.step2Title",
                descKey: "landing.step2Desc",
              },
              {
                kind: "role",
                role: "consumer",
                rotate: true,
                titleKey: "landing.step3Title",
                descKey: "landing.step3Desc",
              },
              {
                kind: "icon",
                Icon: FaTruck,
                titleKey: "landing.step4Title",
                descKey: "landing.step4Desc",
              },
            ].map((step, index) => (
              <li key={step.titleKey} className="how-it-works-step">
                <div className="how-it-works-step-marker">
                  <span className="how-it-works-step-index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="how-it-works-step-icon" aria-hidden>
                    {step.kind === "icon" ? (
                      <step.Icon />
                    ) : (
                      <RoleIllustration
                        role={step.role}
                        rotateConsumer={!!step.rotate}
                        size="md"
                      />
                    )}
                  </span>
                </div>
                <div className="how-it-works-step-body">
                  <h3 className="how-it-works-step-title">
                    {t(step.titleKey)}
                  </h3>
                  <p className="how-it-works-step-desc">{t(step.descKey)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Reviews/Testimonials Section - real reviews from API */}
      <section className="reviews-section">
        <div className="section-container">
          <h2 className="section-title">{t("landing.customersSay")}</h2>
          <div className="reviews-slider">
            {reviews.length > 0 ? (
              reviews.map((review) => (
                <div key={review._id} className="review-card">
                  <div className="review-avatar">
                    {review.consumer?.profileImage ? (
                      <img
                        src={getProfileImageUrl(review.consumer.profileImage)}
                        alt={review.consumer?.name}
                        className="landing-review-avatar-img"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <FaUser
                        className="landing-review-avatar-icon"
                        aria-hidden
                      />
                    )}
                  </div>
                  <div className="review-stars">
                    {[1, 2, 3, 4, 5].map((i) =>
                      i <= (review.rating || 0) ? (
                        <FaStar key={i} aria-hidden />
                      ) : (
                        <FaRegStar key={i} aria-hidden />
                      ),
                    )}
                  </div>
                  <p className="review-text">
                    "{review.comment || t("landing.greatExperience")}"
                  </p>
                  <p className="review-author">
                    – {review.consumer?.name || t("landing.customer")}
                    {review.product?.name && ` · ${review.product.name}`}
                  </p>
                </div>
              ))
            ) : (
              <div className="review-card">
                <div className="review-avatar">
                  <FaUser className="landing-review-avatar-icon" aria-hidden />
                </div>
                <div className="review-stars">
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                </div>
                <p className="review-text">"{t("landing.firstReview")}"</p>
                <p className="review-author">– Taja Bazzar</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Guides & Articles */}
      <section className="landing-guides-section">
        <div className="landing-container">
          <h2 className="section-title">{t("guides.pageTitle")}</h2>
          <p className="section-subtitle">{t("guides.intro")}</p>
          <div className="landing-guides-grid">
            <Link to="/guides/seasonal-produce-guide-province-1" className="landing-guide-card">
              <span className="landing-guide-cat">Seasonal Produce</span>
              <h3>Seasonal Produce Guide for Province 1</h3>
              <p>Know what vegetables and fruits are in season each month in Morang, Sunsari, and Jhapa.</p>
            </Link>
            <Link to="/guides/benefits-of-buying-direct-from-farmers" className="landing-guide-card">
              <span className="landing-guide-cat">Shopping Tips</span>
              <h3>7 Benefits of Buying Direct From Farmers</h3>
              <p>Fresher food, fairer prices, and stronger local communities when you skip the middleman.</p>
            </Link>
            <Link to="/guides/eastern-nepal-farming-traditions" className="landing-guide-card">
              <span className="landing-guide-cat">Local Food</span>
              <h3>Farming Traditions of Eastern Nepal</h3>
              <p>Rice culture, vegetable belts, and the heritage behind Terai agriculture.</p>
            </Link>
          </div>
          <div className="landing-guides-cta">
            <Link to="/guides" className="btn-primary">{t("landing.viewAllGuides")}</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
